import React, { Component, createRef } from "react";
import { ACSC, ROSTER, SAMPLES, SLA_MINUTES, SYSTEM_PROMPT, T, URGENCY } from "./preScreeningConfig";
import SupervisorView from "./SupervisorView";
import { aiTriage, Chip, DomainList, exportCasePDF, fallbackTriage, parseCCDA, parseHL7, ruleFlags } from "./preScreeningComp";
import { formatDate, formatDateTime, getViewPatientData, postFileResource, postResource } from "../../lib";

export default class PreScreeningTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            queue: [],
            log: [],
            audit: [],
            uploadHistory: [],
            careManagerList: [],
            busy: false,
            view: "triage",
            pasteOpen: false,
            pasteText: "",
            expanded: null,
            dismissing: null,
            dismissReason: "",
            loading: false,
            error: null,
            auditLoading: false,
            auditError: null,
            uploadHistoryLoading: false,
            uploadHistoryError: null,
            startingLogOutreach: false,
            dismissingLog: false,
            careManagerSelections: {},
            assigningCareManager: false,
            careManagerDropdownOpen: null,
            careManagerSearch: "",
        };
        this.fileRef = createRef();
        this.idCounter = 0;
    }

    componentDidMount() {
        this.fetchNeedReviewCardData();
        this.fetchAuditData();
        this.fetchUploadHistoryData();
        this.fetchCareManagerList();
    }

    fetchNeedReviewCardData = async () => {
        try {
            this.setState({ loading: true, error: null, queue: [] });
            const data = await getViewPatientData({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/triage/queue`,
                server: this.props.settings.server
            });
            const mapped = (data?.cases || []).map(this.mapApiCaseToItem);
            this.setState({ queue: mapped, loading: false });
        } catch (error) {
            console.error("KPI fetch failed:", error);
            this.setState({ error: error.message, loading: false });
        }
    };

    fetchCareManagerList = async () => {
        try {
            this.setState({ careManagerList: [] });
            const data = await getViewPatientData({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/lcm-members/unique`,
                server: this.props.settings.server
            });
            const members = data?.members || [];
            const seen = new Set();
            const unique = members.filter((m) => {
                if (seen.has(m.lcmName)) return false;
                seen.add(m.lcmName);
                return true;
            });
            this.setState({ careManagerList: unique });
        } catch (error) {
            console.error("Care manager list fetch failed:", error);
        }
    };

    fetchAuditData = async () => {
        try {
            this.setState({ auditLoading: true, auditError: null });
            const data = await getViewPatientData({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/audit`,
                server: this.props.settings.server
            });
            this.setState({ audit: data?.events || [], auditLoading: false });
        } catch (error) {
            console.error("Audit fetch failed:", error);
            this.setState({ auditError: error.message, auditLoading: false });
        }
    };

    fetchUploadHistoryData = async () => {
        try {
            this.setState({ uploadHistoryLoading: true, uploadHistoryError: null });
            const data = await getViewPatientData({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/documents/uploads`,
                server: this.props.settings.server
            });
            this.setState({ uploadHistory: data?.uploads || [], uploadHistoryLoading: false });
        } catch (error) {
            console.error("Upload history fetch failed:", error);
            this.setState({ uploadHistoryError: error.message, uploadHistoryLoading: false });
        }
    };

    assignCareManager = async (pk, sk, caseOwner, lcmName) => {
        this.setState({ assigningCareManager: true });

        try {
            await postResource({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/assign-care-manager`,
                server: this.props.settings.server,
                data: {
                    mrn: pk,
                    documentId: sk,
                    assignedCareManager: caseOwner,
                    assignedCareManagerId: lcmName,
                }
            });

            this.setState((prev) => {
                const sel = { ...prev.careManagerSelections };
                delete sel[sk];
                return { assigningCareManager: false, careManagerSelections: sel };
            }, () => {
                this.fetchNeedReviewCardData();
            });

        } catch (error) {
            console.error("Failed to assign care manager:", error);
            this.setState({ assigningCareManager: false });
        }
    };

    mapApiCaseToItem = (apiCase) => ({
        id: apiCase.documentId,
        documentId: apiCase.documentId,
        mrn: apiCase.mrn,
        status: apiCase.reviewStatus,
        processedDateTime: apiCase.processedDateTime ? new Date(apiCase.processedDateTime).toLocaleString() : null,
        receivedAt: apiCase.receivedDateTime ? new Date(apiCase.receivedDateTime).toLocaleTimeString() : null,
        receivedAtMs: apiCase.receivedDateTime ? new Date(apiCase.receivedDateTime).getTime() : null,
        pk: apiCase?.pk?.replace("PATIENT#", ""),
        sk: apiCase?.sk?.replace("DOCUMENT#", ""),
        careManager: apiCase.assignedCareManager,
        enc: {
            mrn: apiCase.mrn,
            patientName: apiCase.patientName,
            patientGender: apiCase.patientGender,
            patientDob: apiCase.patientDob,
            messageType: apiCase.eventTypeLabel || apiCase.eventType,
            eventTs: apiCase.receivedDateTime ? new Date(apiCase.receivedDateTime).toLocaleString() : null,
            facility: apiCase.facility,
            format: apiCase.documentType,
        },
        triage: {
            urgency: (apiCase.ecmPriority || "routine").toLowerCase(),
            confidence: (apiCase.aiConfidence || 0) / 100,
            primary_reason: apiCase.recommendedFirstAction || apiCase.summary,
            recommended_outreach: apiCase.recommendedFirstAction || "",
            talking_points: apiCase.priorityDiagnoses || [],
            clinical: [],
            behavioral: [],
            social: [],
            fallback: !apiCase.aiAvailable,
        },
        rosterRow: apiCase.assignedCareManager ? {
            tier: apiCase.patientRiskLevel,
            careManager: apiCase.assignedCareManager,
        } : null,
        flags: apiCase.ruleTriggerCount
            ? [`${apiCase.ruleTriggerCount} rule trigger(s) · ${apiCase.criticalEventCount} critical event(s)`]
            : [],
        slaBreached: apiCase.slaBreached,
        slaCategory: apiCase.slaCategory,
        slaDeadline: apiCase.slaDeadline,
        waitingMinutes: apiCase.waitingMinutes,
        patientRiskLevel: apiCase.patientRiskLevel,
        outreachStatus: apiCase.outreachStatus,
        counts: {
            ruleTriggers: apiCase.ruleTriggerCount || 0,
            clinical: apiCase.clinicalRiskCount || 0,
            behavioral: apiCase.behavioralRiskCount || 0,
            sdoh: apiCase.sdohRiskCount || 0,
            careGaps: apiCase.careGapCount || 0,
            criticalEvents: apiCase.criticalEventCount || 0,
        },
        summary: apiCase.summary,
    });

    startLogOutreach = async (pk, sk) => {

        this.setState({ startingLogOutreach: true });

        try {
            await getViewPatientData({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/outreach`,
                server: this.props.settings.server,
                method: 'POST',
                data: {
                    mrn: pk,
                    documentId: sk,
                }
            });

            this.setState({ startingLogOutreach: false }, () => {
                this.fetchNeedReviewCardData();
            });

        } catch (error) {
            console.error("Failed to log outreach:", error);
            this.setState({ startingLogOutreach: false });
        }
    };

    dismissLog = async (pk, sk) => {

        this.setState({ dismissingLog: true });

        try {
            await getViewPatientData({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/dismiss`,
                server: this.props.settings.server,
                method: 'POST',
                data: {
                    mrn: pk,
                    documentId: sk,
                }
            });

            this.setState({ dismissingLog: false }, () => {
                this.fetchNeedReviewCardData();
            });

        } catch (error) {
            console.error("Failed to dismiss log:", error);
            this.setState({ dismissingLog: false });
        }
    };


    uploadFile = async (file) => {
        this.setState({ busy: true });
        try {
            const formData = new FormData();
            formData.append("file", file);

            await postFileResource({
                url: `${this.props.settings.server.screeningUrl}/ecm/pre-screening/documents/upload`,
                server: this.props.settings.server,
                data: formData,
            });

            this.fetchNeedReviewCardData();
        } catch (e) {
            console.error("Upload failed:", e.message);
        }
        this.setState({ busy: false });
    };

    onFiles = async (files) => {
        this.setState({ pendingFiles: Array.from(files) });
    };

    render() {
        const { queue, log, audit, uploadHistory, busy, view, pasteOpen, pasteText, expanded, dismissing, dismissReason, loading, error, auditLoading, auditError, uploadHistoryLoading, uploadHistoryError, careManagerList } = this.state;

        const open = queue.filter((q) => q.status === "Open" && q.outreachStatus === 'Not Started');
        const done = queue.filter((q) => q.status !== "Open" || q.outreachStatus === 'Started');
        console.log("Audit", audit);
        return (
            <div style={{ background: T.paper, fontFamily: T.sans, color: T.ink }}>
                <style>{`
          @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
          * { box-sizing: border-box; }
          button { font-family: inherit; cursor: pointer; }
          button:focus-visible, textarea:focus-visible { outline: 2px solid ${T.pine}; outline-offset: 2px; }
          @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
          @media (prefers-reduced-motion: reduce) { * { animation: none !important; } }
        `}</style>

                {/* Header */}
                <header style={{ background: T.pineDark, color: "#fff", padding: "16px 18px" }}>
                    <div style={{ maxWidth: "100%", margin: "0 auto", display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", padding: "0 24px" }}>
                        <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.2 }}>ECM Rapid Response</div>
                        <div style={{ fontSize: 12.5, opacity: 0.75 }}>Inbound ADT &amp; C-CDA triage</div>
                        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                            {busy && (
                                <span style={{ fontSize: 12, animation: "pulse 1.2s infinite", marginRight: 8 }}>● agent working…</span>
                            )}
                            {["triage", "supervisor"].map((v) => (
                                <button
                                    key={v}
                                    onClick={() => this.setState({ view: v })}
                                    style={{
                                        background: view === v ? "#fff" : "transparent",
                                        color: view === v ? T.pineDark : "#fff",
                                        border: "1px solid rgba(255,255,255,.45)",
                                        borderRadius: 7, padding: "6px 13px", fontSize: 12.5, fontWeight: 700,
                                        textTransform: "capitalize",
                                    }}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>
                </header>

                <main style={{
                    maxWidth: "100%", margin: "0 auto", padding: "16px 24px", height: '100vh', paddingBottom: "10rem", overflow: 'auto', display: "grid",
                    gridTemplateColumns: "minmax(280px, 360px) 1fr",
                    gap: 16,
                }}>
                    {view === "supervisor" && (
                        <SupervisorView
                            query={this.props.query}
                            settings={this.props.settings}
                            location={this.props.location}
                            urlParams={this.props.urlParams}
                            queue={queue}
                            audit={audit}
                            onJumpToTriage={() => this.setState({ view: "triage" })}
                        />
                    )}

                    {view === "triage" && (
                        <>
                            {/* ---- Left: intake + agent log ---- */}
                            <section style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
                                <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, flexDirection: "column" }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); this.setState({ pendingFiles: Array.from(e.dataTransfer.files) }); }}
                                >
                                    <i className="fa fa-file-waveform" style={{ color: T.pine, fontSize: 14, flexShrink: 0 }} />

                                    <span style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.5, color: T.mute, flexShrink: 0 }}>INTAKE</span>

                                    {/* <div style={{ width: 1, height: 16, background: T.line, flexShrink: 0 }} /> */}

                                    <span style={{ fontSize: 12.5, color: T.mute, flexShrink: 0 }}>Drop HL7 / C-CDA or</span>

                                    <button
                                        onClick={() => this.fileRef.current?.click()}
                                        style={{ background: "none", color: T.pine, border: `1px solid ${T.pine}55`, borderRadius: 6, padding: "3px 10px", fontSize: 12.5, fontWeight: 600, flexShrink: 0 }}
                                    >
                                        Choose file
                                    </button>
                                    <input
                                        ref={this.fileRef}
                                        type="file"
                                        accept=".hl7,.txt,.xml,.cda"
                                        style={{ display: "none" }}
                                        onChange={(e) => { this.setState({ pendingFiles: Array.from(e.target.files) }); e.target.value = ""; }}
                                    />

                                    {this.state.pendingFiles?.length > 0 ? (
                                        <>
                                            <div style={{ width: 1, height: 16, background: T.line, flexShrink: 0 }} />
                                            <i className="fa fa-paperclip" style={{ color: T.mute, fontSize: 12, flexShrink: 0 }} />
                                            <span style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                                                {this.state.pendingFiles[0].name}
                                                {this.state.pendingFiles.length > 1 && <span style={{ color: T.mute }}> +{this.state.pendingFiles.length - 1}</span>}
                                            </span>
                                            <button
                                                disabled={busy}
                                                onClick={() => { this.state.pendingFiles.forEach((f) => this.uploadFile(f)); this.setState({ pendingFiles: [] }); }}
                                                style={{ background: T.pine, color: "#fff", border: "none", borderRadius: 6, padding: "4px 14px", fontSize: 12.5, fontWeight: 600, flexShrink: 0, opacity: busy ? 0.5 : 1 }}
                                            >
                                                {busy ? <><i className="fa fa-spinner fa-spin" /> Uploading…</> : "Upload"}
                                            </button>
                                            <button
                                                onClick={() => this.setState({ pendingFiles: [] })}
                                                style={{ background: "none", color: T.mute, border: "none", fontSize: 13, padding: "2px 4px", flexShrink: 0, lineHeight: 1 }}
                                            >✕</button>
                                        </>
                                    ) : (
                                        <span style={{ flex: 1, fontSize: 12, color: T.mute, fontStyle: "italic" }}>or drag &amp; drop here</span>
                                    )}
                                </div>

                                <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14 }}>
                                    <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: T.mute, textTransform: "uppercase" }}>Upload history</div>
                                        <button
                                            onClick={this.fetchUploadHistoryData}
                                            disabled={uploadHistoryLoading}
                                            style={{ marginLeft: "auto", background: "none", border: "none", color: T.pine, fontSize: 12, padding: "2px 6px", opacity: uploadHistoryLoading ? 0.5 : 1 }}
                                        >
                                            <i className={`fa fa-rotate-right ${uploadHistoryLoading ? "fa-spin" : ""}`} /> Refresh
                                        </button>
                                    </div>
                                    {uploadHistoryLoading && <div style={{ fontSize: 12.5, color: T.mute }}><i className="fa fa-spinner fa-spin" /> Loading…</div>}
                                    {!uploadHistoryLoading && uploadHistoryError && <div style={{ fontSize: 12.5, color: T.immediate }}>Failed to load: {uploadHistoryError}</div>}
                                    {!uploadHistoryLoading && !uploadHistoryError && uploadHistory.length === 0 && (
                                        <div style={{ fontSize: 12.5, color: T.mute }}>No uploads yet.</div>
                                    )}
                                    {!uploadHistoryLoading && !uploadHistoryError && uploadHistory.map((u, i) => {
                                        const statusColor = u.status === "COMPLETED" ? T.ok : u.status === "PENDING" ? T.h24 : T.immediate;
                                        return (
                                            <div key={u.uploadId || i} style={{ fontSize: 12.5, padding: "6px 0", borderBottom: i < uploadHistory.length - 1 ? `1px solid ${T.line}` : "none" }}>
                                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                                    <span style={{ fontFamily: T.mono, color: T.mute }}>{formatDateTime(u.uploadedAt)}</span>
                                                    <Chip color={statusColor}>{u.status}</Chip>
                                                    {u.documentType && <Chip color={T.mute}>{u.documentType}</Chip>}
                                                    {u.patientName && <strong>{u.patientName}</strong>}
                                                    {u.mrn && <span style={{ fontFamily: T.mono, color: T.mute }}>MRN {u.mrn}</span>}
                                                </div>
                                                <div style={{ color: T.mute, marginTop: 3 }}>{u.fileName}</div>
                                                {u.errorDetails && <div style={{ color: T.immediate, marginTop: 3 }}>{u.errorDetails}</div>}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14 }}>
                                    <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: T.mute, textTransform: "uppercase" }}>Audit trail</div>
                                        <button
                                            onClick={this.fetchAuditData}
                                            disabled={auditLoading}
                                            style={{ marginLeft: "auto", background: "none", border: "none", color: T.pine, fontSize: 12, padding: "2px 6px", opacity: auditLoading ? 0.5 : 1 }}
                                        >
                                            <i className={`fa fa-rotate-right ${auditLoading ? "fa-spin" : ""}`} /> Refresh
                                        </button>
                                    </div>
                                    {auditLoading && <div style={{ fontSize: 12.5, color: T.mute }}><i className="fa fa-spinner fa-spin" /> Loading…</div>}
                                    {!auditLoading && auditError && <div style={{ fontSize: 12.5, color: T.immediate }}>Failed to load: {auditError}</div>}
                                    {!auditLoading && !auditError && audit.length === 0 && (
                                        <div style={{ fontSize: 12.5, color: T.mute }}>No audit events yet.</div>
                                    )}
                                    {!auditLoading && !auditError && audit
                                        .filter((a) => !["Pending", "Processing", "PENDING", "PROCESSING"].includes(a.newStatus))
                                        .map((a, i, arr) => (
                                            <div key={i} style={{ fontSize: 12.5, padding: "6px 0", borderBottom: i < arr.length - 1 ? `1px solid ${T.line}` : "none" }}>
                                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                                    <span style={{ fontFamily: T.mono, color: T.mute }}>{formatDateTime(a.at)}</span>
                                                    <strong>{a.mrn}</strong>
                                                    <Chip color={a.newStatus === "outreach_logged" ? T.ok : T.mute}>{a.newStatus}</Chip>
                                                </div>
                                                {a.details && <div style={{ color: T.mute, marginTop: 3 }}>"{a.details}"</div>}
                                            </div>
                                        ))}
                                </div>
                            </section>

                            {/* ---- Right: review queue ---- */}
                            <section style={{ minWidth: 0, marginTop: "1rem" }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                                    <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Needs your review</h2>
                                    <span style={{ fontSize: 13, color: T.mute }}>{open.length} case{open.length !== 1 ? "s" : ""} · AI recommends, you decide</span>
                                    <button
                                        onClick={this.fetchNeedReviewCardData}
                                        disabled={loading}
                                        style={{ marginLeft: "auto", background: "none", border: "none", color: T.pine, fontSize: 12, padding: "2px 6px", opacity: loading ? 0.5 : 1 }}
                                    >
                                        <i className={`fa fa-rotate-right ${loading ? "fa-spin" : ""}`} /> Refresh
                                    </button>
                                </div>

                                {loading && (
                                    <div style={{ background: T.card, border: `1px dashed ${T.line}`, borderRadius: 10, padding: 28, textAlign: "center", color: T.mute, fontSize: 14 }}>
                                        <i className="fa fa-spinner fa-spin" /> Fetching Data. Please wait...
                                    </div>
                                )}

                                {!loading && open.length === 0 && (
                                    <div style={{ background: T.card, border: `1px dashed ${T.line}`, borderRadius: 10, padding: 28, textAlign: "center", color: T.mute, fontSize: 14 }}>
                                        Queue is clear. New urgent cases will appear here as files arrive.
                                    </div>
                                )}

                                {open.map((item) => {
                                    const u = URGENCY[item.triage.urgency] || URGENCY.routine;
                                    const isOpen = expanded === item.id;

                                    const riskColor = {
                                        Critical: T.immediate,
                                        High: "#e07b00",
                                        Medium: T.h24,
                                        Low: T.ok,
                                    }[item.patientRiskLevel] || T.mute;

                                    const fmtDob = item.enc.patientDob
                                        ? new Date(item.enc.patientDob).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                        : null;

                                    const fmtDeadline = item.slaDeadline
                                        ? new Date(item.slaDeadline).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                                        : null;

                                    return (
                                        <article
                                            key={item.id}
                                            style={{ background: T.card, border: `1px solid ${item.slaBreached ? T.immediate : T.line}`, borderLeft: `5px solid ${u.color}`, borderRadius: 10, marginBottom: 12 }}
                                        >
                                            {/* ── Card header (always visible) ── */}
                                            <button
                                                onClick={() => this.setState({ expanded: isOpen ? null : item.id })}
                                                style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "13px 15px" }}
                                            >
                                                {/* Row 1: urgency chip + name + MRN + facility */}
                                                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                                    <Chip color={u.color} filled>{u.label}</Chip>
                                                    <strong style={{ fontSize: 15 }}>{item.enc.patientName || "Unknown patient"}</strong>
                                                    {item.enc.patientGender && (
                                                        <span style={{ fontSize: 12, color: T.mute }}>{item.enc.patientGender}</span>
                                                    )}
                                                    {fmtDob && (
                                                        <span style={{ fontSize: 12, color: T.mute }}>DOB {fmtDob}</span>
                                                    )}
                                                    <span style={{ fontFamily: T.mono, fontSize: 12, color: T.mute }}>MRN {item.enc.mrn}</span>
                                                    <span style={{ fontSize: 12, color: T.mute, marginLeft: "auto" }}>{item.enc.facility.replace("\"\"^", "") || item.enc.format}</span>
                                                </div>

                                                {/* Row 2: risk level + SLA + outreach status */}
                                                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 6, position: "relative", overflow: "visible" }}>
                                                    <span style={{ fontSize: 12, fontWeight: 700, color: riskColor, background: riskColor + "18", borderRadius: 5, padding: "2px 8px" }}>
                                                        {item.patientRiskLevel} Risk
                                                    </span>
                                                    {item.slaBreached ? (
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: T.immediate, background: T.immediate + "15", borderRadius: 5, padding: "2px 8px" }}>
                                                            ⚠ SLA · {item.waitingMinutes} min waiting
                                                        </span>
                                                    ) : (
                                                        <span style={{ fontSize: 12, color: T.mute, background: T.line + "60", borderRadius: 5, padding: "2px 8px" }}>
                                                            {item.slaCategory} · {item.waitingMinutes} min waiting
                                                        </span>
                                                    )}
                                                    {item.outreachStatus && (
                                                        <span style={{ fontSize: 12, color: T.mute }}>Outreach: <strong style={{ color: T.ink }}>{item.outreachStatus}</strong></span>
                                                    )}
                                                    {item.careManager && item.careManager != "" ? (
                                                        <span style={{ fontSize: 12, color: T.mute }}>Care Manager: <strong style={{ color: T.ink }}>{item.careManager}</strong></span>
                                                    ) : (
                                                        <span style={{ fontSize: 12, color: T.mute, display: "flex", alignItems: "center", gap: 4, position: "relative", width: "35%" }}>
                                                            Assign Care Manager:
                                                            <div
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    this.setState((prev) => ({
                                                                        careManagerDropdownOpen: prev.careManagerDropdownOpen === item.sk ? null : item.sk,
                                                                        careManagerSearch: "",
                                                                    }));
                                                                }}
                                                                style={{ fontSize: 10, width: '45%', color: T.ink, marginLeft: 4, border: `1px solid ${T.line}`, borderRadius: 5, padding: "3px 6px", background: "#fff", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                                            >
                                                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {this.state.careManagerSelections[item.sk]?.lcmName || "Select an option"}
                                                                </span>
                                                                <i className="fa fa-chevron-down" style={{ fontSize: 9, marginLeft: 4 }} />
                                                            </div>

                                                            {this.state.careManagerDropdownOpen === item.sk && (
                                                                <div
                                                                    style={{ position: "absolute", top: "100%", left: '17%', width: "60%", background: "#fff", border: `1px solid ${T.line}`, borderRadius: 5, boxShadow: "0 2px 8px rgba(0,0,0,.12)", zIndex: 20, maxHeight: 220, overflow: "hidden", display: "flex", flexDirection: "column" }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        autoFocus
                                                                        placeholder="Search…"
                                                                        value={this.state.careManagerSearch}
                                                                        onChange={(e) => this.setState({ careManagerSearch: e.target.value })}
                                                                        style={{ fontSize: 11, padding: "5px 7px", border: "none", borderBottom: `1px solid ${T.line}`, outline: "none", width: "100%" }}
                                                                    />
                                                                    <div style={{ overflowY: "auto" }}>
                                                                        {[...this.state.careManagerList]
                                                                            .sort((a, b) => (a.lcmName || "").localeCompare(b.lcmName || ""))
                                                                            .filter((m) => (m.lcmName || "").toLowerCase().includes(this.state.careManagerSearch.toLowerCase()))
                                                                            .map((manager, index) => (
                                                                                <div
                                                                                    key={manager.caseOwner + index}
                                                                                    onClick={() => {
                                                                                        this.setState((prev) => ({
                                                                                            careManagerSelections: {
                                                                                                ...prev.careManagerSelections,
                                                                                                [item.sk]: { caseOwner: manager.caseOwner, lcmName: manager.lcmName },
                                                                                            },
                                                                                            careManagerDropdownOpen: null,
                                                                                        }));
                                                                                    }}
                                                                                    style={{ fontSize: 11.5, padding: "6px 8px", cursor: "pointer", color: T.ink }}
                                                                                    onMouseEnter={(e) => e.currentTarget.style.background = T.paper}
                                                                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                                                                >
                                                                                    {manager.lcmName}
                                                                                </div>
                                                                            ))}
                                                                        {this.state.careManagerList.filter((m) => (m.lcmName || "").toLowerCase().includes(this.state.careManagerSearch.toLowerCase())).length === 0 && (
                                                                            <div style={{ fontSize: 11.5, padding: "6px 8px", color: T.mute }}>No matches</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {this.state.careManagerSelections[item.sk]?.caseOwner && (
                                                                <button
                                                                    onClick={() => {
                                                                        const sel = this.state.careManagerSelections[item.sk];
                                                                        this.assignCareManager(item.pk, item.sk, sel.caseOwner, sel.lcmName);
                                                                    }}
                                                                    disabled={this.state.assigningCareManager}
                                                                    style={{ background: T.pine, color: "#fff", border: "none", borderRadius: 5, padding: "3px 8px", fontSize: 11, lineHeight: 1, opacity: this.state.assigningCareManager ? 0.5 : 1 }}
                                                                    title="Confirm assignment"
                                                                >
                                                                    <i className={`fa ${this.state.assigningCareManager ? "fa-spinner fa-spin" : "fa-check"}`} />
                                                                </button>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Row 3: recommended action */}
                                                <div style={{ fontSize: 13.5, marginTop: 7, lineHeight: 1.5 }}>{item.triage.primary_reason}</div>
                                                {item.triage.fallback && (
                                                    <div style={{ fontSize: 12, color: T.immediate, marginTop: 4 }}>⚠ AI unavailable — rule-based result, manual review essential</div>
                                                )}

                                                {/* Row 4: risk signal counters */}
                                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                                                    {[
                                                        { label: "Critical Events", val: item.counts.criticalEvents, color: T.immediate },
                                                        { label: "Clinical", val: item.counts.clinical, color: T.clinical },
                                                        { label: "Behavioral", val: item.counts.behavioral, color: T.behavioral },
                                                        { label: "SDOH", val: item.counts.sdoh, color: T.social },
                                                        { label: "Care Gaps", val: item.counts.careGaps, color: T.mute },
                                                        { label: "Rule Triggers", val: item.counts.ruleTriggers, color: T.mute },
                                                    ].map(({ label, val, color }) => val > 0 ? (
                                                        <span key={label} style={{ fontSize: 11.5, color, background: color + "15", borderRadius: 5, padding: "2px 7px", fontWeight: 600 }}>
                                                            {val} {label}
                                                        </span>
                                                    ) : null)}

                                                    <span style={{ marginLeft: "auto", background: "none", border: "none", color: T.pine, fontSize: 12, padding: "2px 6px" }}>
                                                        {formatDateTime(item?.processedDateTime)}
                                                    </span>
                                                </div>
                                            </button>

                                            {/* ── Expanded detail ── */}
                                            {isOpen && (
                                                <div style={{ padding: "0 15px 15px", borderTop: `1px solid ${T.line}` }}>

                                                    {/* Meta row */}
                                                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12.5, color: T.mute, padding: "10px 0 2px" }}>
                                                        <span>Event: <strong style={{ color: T.ink }}>{item.enc.messageType}</strong></span>
                                                        {item.enc.eventTs && <span>Received: <strong style={{ color: T.ink }}>{item.enc.eventTs}</strong></span>}
                                                        {fmtDeadline && (
                                                            <span>SLA Deadline: <strong style={{ color: item.slaBreached ? T.immediate : T.ink }}>{fmtDeadline}</strong></span>
                                                        )}
                                                        {item.rosterRow && <span>ECM: <strong style={{ color: T.ink }}>{item.rosterRow.tier} · CM {item.rosterRow.careManager}</strong></span>}
                                                        <span>AI confidence: <strong style={{ color: T.ink }}>{Math.round((item.triage.confidence || 0) * 100)}%</strong></span>
                                                    </div>

                                                    <DomainList label="Clinical" color={T.clinical} items={item.triage.clinical} />
                                                    <DomainList label="Behavioral" color={T.behavioral} items={item.triage.behavioral} />
                                                    <DomainList label="Social / SDOH" color={T.social} items={item.triage.social} />

                                                    {/* Recommended action + diagnoses */}
                                                    <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: 8, padding: "10px 12px", marginTop: 12 }}>
                                                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.pine, textTransform: "uppercase", marginBottom: 4 }}>Recommended first action</div>
                                                        <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{item.triage.recommended_outreach}</div>
                                                        {item.triage.talking_points?.length > 0 && (
                                                            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                                                                {item.triage.talking_points.map((t, i) => (
                                                                    <li key={i} style={{ fontSize: 13, lineHeight: 1.5, color: T.ink }}>{t}</li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>

                                                    {/* AI summary */}
                                                    {item.summary && (
                                                        <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: 8, padding: "10px 12px", marginTop: 10 }}>
                                                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.mute, textTransform: "uppercase", marginBottom: 4 }}>Summary</div>
                                                            <div style={{ fontSize: 12.5, lineHeight: 1.6, color: T.ink }}>{item.summary}</div>
                                                        </div>
                                                    )}

                                                    {item.flags.length > 0 && (
                                                        <details style={{ marginTop: 10 }}>
                                                            <summary style={{ fontSize: 12.5, color: T.mute, cursor: "pointer" }}>Rule triggers ({item.counts.ruleTriggers})</summary>
                                                            {item.flags.map((f, i) => (
                                                                <div key={i} style={{ fontFamily: T.mono, fontSize: 12, padding: "2px 0 2px 14px", color: T.ink }}>{f}</div>
                                                            ))}
                                                        </details>
                                                    )}

                                                    {/* HITL decision bar */}
                                                    <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                                                        <button
                                                            onClick={() => this.startLogOutreach(item.pk, item.sk)}
                                                            style={{ background: T.pine, color: "#fff", border: "none", borderRadius: 7, padding: "9px 16px", fontSize: 13.5, fontWeight: 600 }}
                                                        >
                                                            {this.state.startingLogOutreach ? <><i className="fa fa-spinner fa-spin" /> Logging outreach…</> : "Log outreach started"}
                                                        </button>
                                                        <button
                                                            onClick={() => this.dismissLog(item.pk, item.sk)}
                                                            style={{ background: "none", color: T.mute, border: `1px solid ${T.line}`, borderRadius: 7, padding: "9px 16px", fontSize: 13.5, fontWeight: 600 }}
                                                        >
                                                            {this.state.dismissingLog ? <><i className="fa fa-spinner fa-spin" /> Dismissing…</> : "Dismiss"}
                                                        </button>
                                                        <button
                                                            onClick={() => exportCasePDF(item)}
                                                            style={{ background: "none", color: T.pine, border: `1px solid ${T.pine}66`, borderRadius: 7, padding: "9px 16px", fontSize: 13.5, fontWeight: 600, marginLeft: "auto" }}
                                                        >
                                                            Export PDF
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}

                                {done.length > 0 && (
                                    <>
                                        <h3 style={{ fontSize: 13, fontWeight: 700, color: T.mute, textTransform: "uppercase", letterSpacing: 1, margin: "20px 0 8px" }}>Resolved this session</h3>
                                        {done.map((item) => (
                                            <div key={item.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", background: T.card, border: `1px solid ${T.line}`, borderRadius: 8, padding: "9px 13px", marginBottom: 7, fontSize: 13 }}>
                                                <Chip color={item.status === "Dismissed" ? T.mute : T.ok}>
                                                    {item.status === "Dismissed" ? "Dismissed" : item.outreachStatus || item.status}
                                                </Chip>
                                                <strong>{item?.enc?.patientName}</strong>
                                                <span style={{ fontFamily: T.mono, fontSize: 12, color: T.mute }}>{item?.enc?.mrn}</span>
                                                <span style={{ color: T.mute, marginLeft: "auto", fontSize: 12 }}>{item?.enc?.messageType}</span>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </section>
                        </>
                    )}
                </main>
            </div>
        );
    }
}